/**
 * Snapshot 三 module fixture · S2 Day 8b
 *
 * 承接：apps/api/src/csa_api/admin/snapshot_admin.py（Day 8a · 9 endpoint · PR #27）
 *
 * 设计意图：
 * - 内存 store 模拟文件系统 · save 写入 + list 读出 + restore 200
 * - 三 module 各自独立 store（playbook 按 pb_id 隔离）
 * - mock 模式下点 save → 立即 list 看到，restore 立即返 200（验 UI 链路 + 二次确认 Dialog）
 */

import type {
	SnapshotListResponse,
	SnapshotMeta,
	SnapshotRestoreResponse,
	SnapshotSaveRequest,
	SnapshotSaveResponse,
} from "@/lib/api/snapshot";

import { registerFixture } from "./registry";
import type { MockResponse } from "./types";

interface Store {
	[snapshot_id: string]: SnapshotMeta;
}

const STORES: Record<string, Store> = {
	l2_rules: {},
	intent_mapping: {},
};

function playbookStore(pbId: string): Store {
	const key = `playbook:${pbId}`;
	const existing = STORES[key];
	if (existing) return existing;
	const fresh: Store = {};
	STORES[key] = fresh;
	return fresh;
}

function namedStore(kind: "l2_rules" | "intent_mapping"): Store {
	const s = STORES[kind];
	if (s) return s;
	const fresh: Store = {};
	STORES[kind] = fresh;
	return fresh;
}

function makeMeta(author: string, message: string): SnapshotMeta {
	const ts = new Date();
	const iso = `${ts.toISOString().replace(/[:.]/g, "-").slice(0, 19)}Z`;
	const slug =
		message
			.replace(/[^a-zA-Z0-9_-]+/g, "-")
			.replace(/^-|-$/g, "")
			.slice(0, 40) || "msg";
	const aslug = author.replace(/[^a-zA-Z0-9_-]+/g, "-").slice(0, 20);
	const id = `${iso}_${aslug}_${slug}`;
	return {
		snapshot_id: id,
		timestamp: ts.toISOString(),
		author,
		message,
		file_basename: `${id}.yaml`,
		size_bytes: 4096 + Math.floor(Math.random() * 1000),
	};
}

function listResponse(store: Store): SnapshotListResponse {
	const items = Object.values(store).sort(
		(a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
	);
	return { items, total: items.length };
}

function bindModule(
	pathPrefix: string,
	resolveStore: (params: Record<string, string>) => Store,
): void {
	registerFixture<SnapshotListResponse>({
		pathname: `${pathPrefix}/list`,
		methods: ["GET"],
		resolve: (ctx) => ({
			status: 200,
			data: listResponse(resolveStore(ctx.params)),
		}),
	});

	registerFixture<SnapshotSaveResponse>({
		pathname: `${pathPrefix}/save`,
		methods: ["POST"],
		resolve: (ctx) => {
			const body = (ctx.body ?? {}) as SnapshotSaveRequest;
			const store = resolveStore(ctx.params);
			const meta = makeMeta(body.author ?? "anon", body.message ?? "msg");
			store[meta.snapshot_id] = meta;
			return {
				status: 200,
				data: { snapshot: meta, bytes_written: meta.size_bytes },
			};
		},
	});

	registerFixture<SnapshotRestoreResponse>({
		pathname: `${pathPrefix}/restore/:snapshot_id`,
		methods: ["POST"],
		resolve: (ctx) => {
			const store = resolveStore(ctx.params);
			const id = ctx.params.snapshot_id ?? "";
			const meta = store[id];
			if (!meta) {
				// fixture 中 404 用任意 status；UI 层 axios 会转 reject，由 useMutation.isError 处理
				return {
					status: 404,
					data: {
						snapshot_id: id,
						restored_at: new Date().toISOString(),
						bytes_written: 0,
						cache_invalidated: false,
					},
				};
			}
			return {
				status: 200,
				data: {
					snapshot_id: id,
					restored_at: new Date().toISOString(),
					bytes_written: meta.size_bytes,
					cache_invalidated: true,
				},
			};
		},
	});
}

export function registerSnapshotFixtures(): void {
	bindModule("/admin/v1/snapshots/l2_rules", () => namedStore("l2_rules"));
	bindModule("/admin/v1/snapshots/intent_mapping", () => namedStore("intent_mapping"));
	bindModule("/admin/v1/snapshots/playbook/:pb_id", (params) => playbookStore(params.pb_id ?? ""));
}

/** 测试用：清 store · clearFixtures 不会清这里的数据 */
export function _clearSnapshotStores(): void {
	for (const key of Object.keys(STORES)) delete STORES[key];
	STORES.l2_rules = {};
	STORES.intent_mapping = {};
}

// 避免未用 import 报错（Mock fixture 里只用作类型保留）
export type _MockResponseHelper = MockResponse<SnapshotRestoreResponse>;
